import { Button, Input, Modal, Popconfirm, Spin } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { ReactSVG } from 'react-svg';
import { PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../../constants';
import { selectAllRoutePlans } from '../../modules/route-plans/stores/slice';
import {
  doCreateRoutePlan,
  doDeleteRoutePlan,
  doGetRoutePlans,
  doUpdateRoutePlan,
} from '../../modules/route-plans/stores/thunk';
import RoutePlansFilter from '../Forms/Filters/RoutePlansFilter';
import RoutePlanForm from '../Forms/RoutePlans/RoutePlanForm';
import RoutePlansTable from '../Tables/RoutePlansTable';
import withPage from './withPage';
import usePageView from '../../hooks/usePageView';

const RoutesPage = ({ query, pushTo }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const isFetching = useSelector((state) => state.routePlan.isFetching);
  const action = useSelector((state) => state.routePlan.action);
  const status = useSelector((state) => state.routePlan.status);
  const metadata = useSelector((state) => state.routePlan.metadata);
  const idCreate = useSelector((state) => state.routePlan.idCreate);
  const routePlans = useSelector((state) => selectAllRoutePlans(state));

  const [searchValue, setSearchValue] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [routePlanDetail, setRoutePlanDetail] = useState();

  const currentPage = Number(query.current) || 1;
  const itemsPerPage = Number(query.pageSize) || PAGE_SIZE;

  // Emit page view event
  usePageView(window.location.href);

  useEffect(() => {
    setSearchValue(query.q || '');
  }, []);

  const pagination = {
    current: currentPage,
    pageSize: itemsPerPage,
    locale: { items_per_page: 'Routes/Page' },
    total: metadata?.totalItems || 0,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    showSizeChanger: true,
    showTotal: (total) => `${total} Routes`,
  };

  useEffect(() => {
    dispatch(doGetRoutePlans({ page: currentPage, take: itemsPerPage, ...query }));
  }, []);

  useEffect(() => {
    if (['create'].includes(action) && status === 'succeeded' && idCreate) {
      cancelModal();
      history.push(`/routes/${idCreate}`);
    }
  }, [isFetching, idCreate]);

  useEffect(() => {
    if (['delete', 'update'].includes(action) && status === 'succeeded') {
      dispatch(doGetRoutePlans({ page: currentPage, take: itemsPerPage, ...query }));
    }
  }, [isFetching, idCreate]);

  const handleFilter = (params, updatePath) => {
    delete params.pageSizeOptions;
    delete params.showSizeChanger;
    delete params.total;
    delete params.locale;
    delete params.page;
    if (updatePath) {
      pushTo({ query: params });
    }
    const newCurrentPage = Number(params?.current || 1);
    const newItemsPerPage = Number(params?.pageSize) || PAGE_SIZE;
    dispatch(
      doGetRoutePlans({
        page: newCurrentPage,
        take: newItemsPerPage,
        ...params,
      }),
    );
  };

  const cancelModal = () => {
    setIsModalOpen(false);
  };

  const handleFinishRoute = async (params) => {
    try {
      if (!routePlanDetail) {
        await dispatch(doCreateRoutePlan({ ...params }));
      } else {
        await dispatch(doUpdateRoutePlan({ id: routePlanDetail.slug, ...params }));
        setRoutePlanDetail(undefined);
        setIsModalOpen(false);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch && handleSearch();
    }
  };

  const handleChangeSearch = (e) => {
    setSearchValue(e.target.value);
  };

  const handleSearch = () => {
    handleFilter({ q: searchValue || '' }, true);
  };

  const handleTableChange = (params) => {
    const newParams = { ...query, ...params };
    handleFilter(newParams, true);
  };

  const onClickEditRoute = (slug) => {
    const routePlan = routePlans.find((r) => r.slug == slug);
    setRoutePlanDetail(routePlan);
    setIsModalOpen(true);
  };

  const handleClickDelete = async (id) => {
    dispatch(doDeleteRoutePlan({ id }));
  };

  const withAction = (record) => {
    return (
      <div className="actions">
        <Button type="link" className="edit" onClick={() => onClickEditRoute(record.slug)}>
          <ReactSVG className="icon" src="/icons/edit.svg" />
        </Button>
        {!record.totalDrivers && !record.totalStops && (
          <Popconfirm
            title="Are you sure to delete this?"
            onConfirm={() => handleClickDelete(record.slug)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" className="delete">
              <ReactSVG className="icon" src="/icons/delete.svg" />
            </Button>
          </Popconfirm>
        )}
      </div>
    );
  };

  const onClickNewRider = () => {
    setIsModalOpen(true);
    setRoutePlanDetail(undefined);
  };

  return (
    <Spin spinning={isFetching}>
      <div className="RoutesPage Content">
        <div className="ContentHeader">
          <div className="HeaderTitle">Routes</div>
          <div className="HeaderToolBar">
            <div className="ToolBarButtons">
              <Button className="CreateButton ButtonPrimary" type="primary" size="small" onClick={onClickNewRider}>
                Create New Route
                <ReactSVG className="icon" src="/icons/plus.svg" />
              </Button>
            </div>
          </div>
        </div>
        <div className="orders-header-table page-header-table">
          <div className="search">
            <Input
              onChange={handleChangeSearch}
              onBlur={handleSearch}
              placeholder="Search"
              onKeyDown={handleKeyDown}
              suffix={<img src="/icons/search.svg" />}
              disabled={isFetching}
              value={searchValue}
            />
          </div>
          <div className="button-search">
            <Button className="FilterButton ButtonFilter" onClick={() => setIsFilterOpen(!isFilterOpen)}>
              Filter
              <ReactSVG className="icon" src="/icons/filter_list.svg" />
            </Button>
          </div>
        </div>
        {isFilterOpen && <RoutePlansFilter isFetching={isFetching} query={query} onSubmit={handleFilter} />}
        <div className="ContentMain">
          <RoutePlansTable
            onChange={handleTableChange}
            dataSource={routePlans}
            pagination={pagination}
            withAction={withAction}
            rowKey="id"
            isFetching={isFetching}
          />
        </div>
      </div>
      <Modal
        width={552}
        closeIcon={false}
        open={isModalOpen}
        forceRender
        title={routePlanDetail ? 'Update Route' : 'Create New Route'}
      >
        <RoutePlanForm
          isFetching={isFetching}
          onCancel={cancelModal}
          handleFinish={handleFinishRoute}
          routePlan={routePlanDetail}
          forceRender
        />
      </Modal>
    </Spin>
  );
};

export default withPage(RoutesPage);
